import { intersection } from "lodash";
import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";

import { AllergiesKind, PlannedMealItemAttributes } from "@/gql/graphql";
import { getPortionWeightGrams } from "@/utils/food";

import { Cell } from "./Cell";
import MealPlanFormStore from "./MealPlanFormStore";

export class PlannedMealItem {
  uuid: string;
  amount: number;
  food: {
    id: string;
    source: string;
    name: string;
    category: string;
    epWeightGrams: number;
    energyKcal: number;
    proteinGrams: number;
    fatGrams: number;
    carbohydrateGrams: number;
    allergies?: AllergiesKind[] | null | undefined;
  };
  foodPortion: {
    id: string;
    amount: number;
    unitWithModifier: string;
    weightGrams: number;
  };
  cell: Cell;

  private store: MealPlanFormStore;

  get weightGrams(): number {
    return getPortionWeightGrams(this.foodPortion, this.amount);
  }

  get weightPercent(): number {
    return this.weightGrams / this.food.epWeightGrams;
  }

  get energyKcal(): number {
    return this.food.energyKcal * this.weightPercent;
  }

  get allergies(): AllergiesKind[] {
    return intersection(this.food.allergies, this.store.clientAllergies);
  }

  get hasAllergies(): boolean {
    return this.allergies.length !== 0;
  }

  constructor(
    store: MealPlanFormStore,
    cell: Cell,
    food: PlannedMealItem["food"],
    foodPortion: PlannedMealItem["foodPortion"],
    amount: number,
    uuid = uuidv4()
  ) {
    makeAutoObservable(this);
    this.store = store;
    this.cell = cell;
    this.uuid = uuid;
    this.food = food;
    this.foodPortion = foodPortion;
    this.amount = amount;
  }

  setFoodPortion(foodPortion: PlannedMealItem["foodPortion"]): void {
    this.foodPortion = foodPortion;
  }

  incrementAmount(): void {
    this.setAmount(this.amount + 1);
  }

  decrementAmount(): void {
    this.setAmount(this.amount - 1);
  }

  setAmount(value: number): void {
    this.amount = value;
    if (this.amount <= 0) this.remove();
  }

  remove(): void {
    this.cell.removePlannedMealItem(this);
  }

  toAttributes(): PlannedMealItemAttributes {
    return {
      uuid: this.uuid,
      foodId: this.food.id,
      foodPortionId: this.foodPortion.id,
      amount: this.amount,
      day: this.cell.column.day,
    };
  }
}
